import React, { useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { AdCreative, AssetApprovalStatus } from '@marketing-milk/interfaces'
import { CreativeMedia } from '../../creatives/CreativeCard/CreativeMedia'
import { StatusPill } from './StatusPill'
import { VerdictButton } from './VerdictButton'
import { CreativeCardEditing } from '../editor/sections/CreativeCardList'
import { dark, radius, type as typo } from '../tokens'

type Props = {
  creative: AdCreative
  /** Open the full-size preview on this ad (view mode). */
  onOpen: () => void
  /** Open the full-size preview on this ad with the reject-reason form already showing. */
  onOpenReject: () => void
  /**
   * Record an approval. Supplied by the page, which owns the one mutation for the
   * whole grid — the card must not open its own, or the payload it sends drifts
   * from the modal's.
   */
  onApprove: () => void
  submitting: boolean
  /** True when the AE placed this ad in its theme by hand. */
  pinned?: boolean
  /** Present when the viewer may author the strategy. Absent for a client. */
  editing?: CreativeCardEditing
}

/** Media-tile overlay chrome; each usage sets its own background. */
const overlayChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 8px',
  borderRadius: radius.sm,
  color: '#fff',
  border: 'none',
  fontFamily: typo.family.sans,
  fontSize: typo.size.eyebrow,
  fontWeight: typo.weight.semibold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}

/**
 * One ad in the client review grid: thumbnail, current status, and quick
 * Approve/Reject actions. Clicking the card (or its Reject button) opens the
 * full-size preview with the ad's copy/metadata alongside the same actions —
 * see `ReviewCreativePreviewModal`, rendered once by the parent page (not
 * here) so a successful decision there can advance straight into the next
 * card's ad instead of just closing. The quick Approve button below is a
 * separate, immediate action that never opens anything.
 *
 * A published ad is live on Facebook, so neither verdict means anything and the
 * server refuses both — the actions are hidden rather than left to 400.
 *
 * With `editing` the tile also carries the per-ad half of theme authoring —
 * moving the ad to another theme, and clearing a hand-placed pin. That is the
 * same standard the preview modal already sets by letting an employee edit copy
 * and media in front of the client.
 */
export function ReviewCreativeCard({
  creative,
  onOpen,
  onOpenReject,
  onApprove,
  submitting,
  pinned,
  editing,
}: Props) {
  const [hovered, setHovered] = useState(false)
  const [moveHovered, setMoveHovered] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const latestExternalReview = [...creative.reviews]
    .filter(review => review.type === 'external')
    .sort((a, b) => b.createdAt - a.createdAt)[0]

  const canReview = creative.reviewStatus !== AssetApprovalStatus.Published
  const isApproved = creative.reviewStatus === AssetApprovalStatus.Approved
  const isRejected = creative.reviewStatus === AssetApprovalStatus.Rejected

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // No shadow: on the near-black ground a drop shadow is invisible, so
        // elevation comes from the surface lift, and hover moves the border to
        // the accent instead.
        border: `1px solid ${hovered ? dark.accent : dark.hairline}`,
        borderRadius: radius.lg,
        overflow: 'hidden',
        background: dark.surface,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-3px)' : 'none',
        transition: 'border-color 0.15s ease, transform 0.15s ease',
      }}
    >
      <div style={{ position: 'relative', background: dark.bg }}>
        <CreativeMedia creative={creative} className="h-48 w-full" />
        <div style={{ position: 'absolute', top: 10, left: 10 }}>
          <StatusPill creative={creative} />
        </div>
        {hovered && !moveHovered && !menuAnchor && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.25)',
              color: '#fff',
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              fontWeight: typo.weight.semibold,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            View ad
          </div>
        )}

        {/* Portal events still bubble through the card, so the wrapper also
          keeps selecting a destination from opening the ad preview. */}
        {editing && editing.groups.length > 0 && (
          <div
            style={{ position: 'absolute', top: 10, right: 10 }}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label={`Change theme for ${creative.name}`}
              title="Change theme"
              aria-haspopup="menu"
              aria-expanded={!!menuAnchor}
              onClick={event => setMenuAnchor(event.currentTarget)}
              onMouseEnter={() => setMoveHovered(true)}
              onMouseLeave={() => setMoveHovered(false)}
              style={{
                background: moveHovered || menuAnchor ? dark.accent : dark.surface,
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: radius.lg,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 32,
                padding: '5px 10px',
                fontFamily: typo.family.sans,
                fontSize: 12,
                fontWeight: typo.weight.semibold,
                transition: 'background 0.15s ease',
                lineHeight: 1.4,
              }}
            >
              Theme
            </button>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              MenuListProps={{ dense: true, 'aria-label': 'Change theme' }}
            >
              <li
                role="presentation"
                style={{ padding: '8px 16px', fontWeight: typo.weight.semibold }}
              >
                Change theme
              </li>
              {editing.groups.map(group => {
                const current = group.ads.some(ad => ad.creativeID === creative.id)
                return (
                  <MenuItem
                    key={group.id}
                    disabled={current && pinned}
                    selected={current}
                    onClick={() => {
                      setMenuAnchor(null)
                      editing.onMove(creative.id, group.id)
                    }}
                  >
                    {group.label}
                    {current && (
                      <i className="fa fa-check" aria-hidden="true" style={{ marginLeft: 8 }} />
                    )}
                  </MenuItem>
                )
              })}
            </Menu>
          </div>
        )}

        {/* Pins have to be visible AND undoable — a pin the AE can't clear is a
          trap, since regeneration would never move the ad again. A client sees
          the same marker, inert. */}
        {pinned && (
          <div style={{ position: 'absolute', bottom: 10, left: 10 }}>
            {editing ? (
              <button
                type="button"
                aria-label={`Unpin ${creative.name}`}
                title="Pinned here by hand — click to unpin"
                onClick={event => {
                  event.stopPropagation()
                  editing.onUnpin(creative.id)
                }}
                style={{ ...overlayChip, background: 'rgba(0,0,0,0.7)', cursor: 'pointer' }}
              >
                <i className="fa fa-thumbtack" style={{ fontSize: 10 }} />
                Pinned
              </button>
            ) : (
              <div
                style={{
                  ...overlayChip,
                  background: 'rgba(0,0,0,0.55)',
                  pointerEvents: 'none',
                }}
              >
                <i className="fa fa-thumbtack" style={{ fontSize: 10 }} />
                Pinned
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            fontFamily: typo.family.sans,
            fontWeight: typo.weight.semibold,
            fontSize: typo.size.bodyLg,
            color: dark.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {creative.name}
        </div>

        {/* The same buttons the modal uses, so a card and the ad it opens can't
          disagree about what a filled verdict means. Clicking the card is what
          opens the ad, so these stop the event before it bubbles.

          Neither carries an `undo`: lifting an approval is an employee
          capability the page only hands to the modal, and re-pressing Reject
          needs the reason field, which lives there too. A recorded verdict here
          is a state, and pressing Reject on it opens the ad where it can be
          revised. */}
        {canReview && (
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }} onClick={e => e.stopPropagation()}>
            <VerdictButton
              verdict="approve"
              block
              recorded={isApproved}
              onClick={isApproved ? undefined : onApprove}
              disabled={submitting || isApproved}
              dimmed={submitting}
            />
            <VerdictButton
              verdict="reject"
              block
              recorded={isRejected}
              onClick={onOpenReject}
              disabled={submitting}
              dimmed={submitting}
            />
          </div>
        )}

        {/* Under the verdicts, not above them: the note is the reason for the
          state the buttons are showing, so it reads as "rejected — here's why"
          rather than as a caption on the ad. It also keeps the button row at the
          same height on every card, where a note above pushed one card's
          verdicts out of line with its neighbours'.

          Latest *external* review only, so a staff rejection shows a Rejected
          pill with no reason under it. That gap is deliberate — the full history
          is in the modal's column. */}
        {latestExternalReview && !latestExternalReview.approved && latestExternalReview.notes && (
          <div
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              color: dark.textMuted,
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            “{latestExternalReview.notes}”
          </div>
        )}
      </div>
    </div>
  )
}
